import { PdfDownloadIcon } from './PdfDownloadButton';
import { PDF_DOWNLOAD_LABEL } from '../../shared/pdfDownload';
import { InvoiceKindChip, InvoiceKindSelect } from './InvoiceKind';
// Invoice detail at ≤880px (mobile overhaul Phase C §8.2, mockup frames F/G).
// The page read as a payment brief: header, the amount card with the overdue
// chrome, the two actions worth a button, the two note blocks, then the
// desktop rail in its own order as FieldRows. The desktop `.detail-grid` and
// `.detail-rail` are not rendered at this width.
//
// It owns no data: pages/InvoiceDetail.tsx keeps every fetch, handler and
// modal and hands them down, so both renders always PATCH the same way. The
// pure helpers both sides need live in lib/invoiceDetailShared.ts — importing
// them from the page would make an ESM cycle.
//
// Nothing here is gesture-only (decision 6): the status-gated actions all sit
// in the "···" sheet (lib/invoiceActions.ts derives which of them a status
// allows, mirroring the desktop header), and every field opens its own
// FieldSheet / DateSheet / PickerSheet rather than swapping to an input.

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  Brand,
  ClientWithCounts,
  InvoiceRow,
  InvoiceLineRow,
  InvoiceStatus,
  ProjectWithClient,
} from '../../shared/types';
import type { MoneyMode } from '../lib/money';
import { displayCents } from '../lib/money';
import { grossFromEx, gstOnEx } from '../../shared/finance';
import { formatDate, formatProjectNumber } from '../lib/calendar';
import { formatMoneyExact, parseMoney } from '../lib/moneyFormat';
import { amountFigures, amountLabel, linkLabel } from '../lib/invoiceDetailShared';
import { invoiceActionsFor, type InvoiceActionKey } from '../lib/invoiceActions';
import { AccordionGroup } from './AccordionGroup';
import { DateSheet } from './DateSheet';
import { FieldRow, FieldSheet, type FieldKind } from './FieldSheet';
import {
  BackIcon,
  CheckIcon,
  DotsIcon,
  LinkIcon,
  LostIcon,
  MOutreachIcon,
  TrashIcon,
} from './icons';
import { PageMenu, type PageMenuItem } from './PageMenu';
import { PickerSheet } from './PickerSheet';
import { NotesBlock, Pill, Seg, Timeline, TimelineRow } from './primitives';
import { Sheet } from './Sheet';

/** The invoice row with the joined client/project columns the page fetches.
    Declared structurally so this file never imports the page (ESM cycle). */
type InvoiceDetailRow = InvoiceRow & {
  lines?: InvoiceLineRow[];
  client_name: string;
  client_contact_name: string | null;
  client_contact_email: string | null;
  client_contact_phone: string | null;
  project_name: string | null;
  project_number: number | null;
  project_brand: Brand | null;
};

export interface InvoiceDetailMobileProps {
  invoice: InvoiceDetailRow;
  linesSection: ReactNode;
  /** Display status (Finance's displayStatus) — a sent invoice past due reads as overdue. */
  status: InvoiceStatus;
  statusLabel: string;
  statusTone: 'accent' | 'gold' | 'danger' | undefined;
  statusItems: readonly { value: InvoiceStatus; label: string }[];
  mode: MoneyMode;
  /** "#0244". */
  number: string;
  effectiveBrand: Brand;
  overdueDays: number | null;
  subBits: string[];
  activity: { id: number; kind: string; summary: string; created_at: number }[];
  tlDate: (ms: number) => string;
  clients: ClientWithCounts[] | null;
  ensureClients: () => void;
  projects: ProjectWithClient[] | null;
  ensureProjects: () => void;
  patch: (fields: Record<string, unknown>) => Promise<void>;
  reminderBusy: boolean;
  linkBusy: boolean;
  sendBusy: boolean;
  /** Transient result of "Copy public link" / "Send invoice" — their sheets close on select. */
  actionNote: string | null;
  sendReminder: () => Promise<void>;
  copyPublicLink: () => Promise<void>;
  sendInvoiceEmail: () => Promise<void>;
  /** Bump the number editor's openSignal — the renumber field sheet lives on the page. */
  onOpenNumber: () => void;
  onTrash: () => Promise<void>;
}

const NO_PROJECT_VALUE = '__no_project__';

/** A single-field sheet opened from any row. */
interface FieldEdit {
  title: string;
  context?: string;
  kind: FieldKind;
  value: string;
  placeholder?: string;
  onSave: (raw: string) => void;
}
/** A calendar sheet opened from any date row. */
interface DateEdit {
  title: string;
  context?: string;
  value: string | null;
  allowClear?: boolean;
  onPick: (iso: string | null) => void;
}

type Sub =
  | { kind: 'status' }
  | { kind: 'client' }
  | { kind: 'project' }
  | { kind: 'void' }
  | { kind: 'trash' };

export function InvoiceDetailMobile(props: InvoiceDetailMobileProps) {
  const {
    invoice,
    linesSection,
    status,
    statusLabel,
    statusTone,
    statusItems,
    mode,
    number,
    effectiveBrand,
    overdueDays,
    subBits,
    activity,
    tlDate,
    clients,
    ensureClients,
    projects,
    ensureProjects,
    patch,
    reminderBusy,
    linkBusy,
    sendBusy,
    actionNote,
    sendReminder,
    copyPublicLink,
    sendInvoiceEmail,
    onOpenNumber,
    onTrash,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [sub, setSub] = useState<Sub | null>(null);
  const [fieldEdit, setFieldEdit] = useState<FieldEdit | null>(null);
  const [dateEdit, setDateEdit] = useState<DateEdit | null>(null);

  const [trashBusy, setTrashBusy] = useState(false);
  const [trashError, setTrashError] = useState<string | null>(null);
  const closeSub = () => {
    if (trashBusy) return;
    setSub(null);
    setTrashError(null);
  };
  const confirmTrash = async () => {
    if (trashBusy) return;
    setTrashBusy(true);
    setTrashError(null);
    try { await onTrash(); }
    catch (error) {
      setTrashError(error instanceof Error ? error.message : 'Could not move the invoice to trash.');
      setTrashBusy(false);
    }
  };
  const openField = (edit: FieldEdit) => setFieldEdit(edit);
  const openDate = (edit: DateEdit) => setDateEdit(edit);

  const actions = invoiceActionsFor(status);
  const can = (key: InvoiceActionKey) => actions.includes(key);
  const context = subBits.join(' · ');
  const lastReminder = invoice.last_reminder_at
    ? `Last sent ${formatDate(new Date(invoice.last_reminder_at).toISOString().slice(0, 10))}`
    : undefined;

  // ── "···" sheet (mockup §8.2 G) — the status-gated actions, in one order ──
  const menuItems: PageMenuItem[] = [
    {
      key: 'download-pdf',
      icon: <PdfDownloadIcon />,
      label: PDF_DOWNLOAD_LABEL,
      onSelect: () => window.open(
        `/api/admin/finance/invoices/${encodeURIComponent(invoice.id)}/pdf`,
        '_blank',
        'noopener,noreferrer',
      ),
    },
    ...(can('record-payment')
      ? [
          {
            key: 'record-payment',
            icon: <span className="inv-ico-acc"><CheckIcon /></span>,
            label: 'Record payment',
            onSelect: () => void patch({ status: 'paid' }),
          },
        ]
      : []),
    ...(can('send-reminder')
      ? [
          {
            key: 'send-reminder',
            icon: <MOutreachIcon />,
            label: 'Send reminder',
            sublabel: lastReminder,
            disabled: reminderBusy,
            onSelect: () => void sendReminder(),
          },
        ]
      : []),
    ...(can('send-invoice')
      ? [
          {
            key: 'send-invoice',
            icon: <MOutreachIcon />,
            label: 'Send invoice',
            disabled: sendBusy,
            onSelect: () => void sendInvoiceEmail(),
          },
        ]
      : []),
    ...(can('copy-link')
      ? [
          {
            key: 'copy-link',
            icon: <LinkIcon />,
            label: 'Copy public link',
            disabled: linkBusy,
            onSelect: () => void copyPublicLink(),
          },
        ]
      : []),
    ...(can('void')
      ? [
          {
            key: 'void',
            icon: <LostIcon />,
            label: 'Void invoice',
            onSelect: () => setSub({ kind: 'void' }),
          },
        ]
      : []),
    {
      key: 'trash',
      icon: <TrashIcon />,
      label: 'Move invoice to trash',
      tone: 'danger',
      onSelect: () => setSub({ kind: 'trash' }),
    },
  ];

  // ── Amount card ─────────────────────────────────────────────────────────
  const figures = amountFigures(invoice, status, mode);

  /** Mirrors the desktop rail's InlineMoney commit: ex-GST entry stores the
      gross plus its 10% component, GST-free invoices keep a zero component. */
  const saveAmount = (raw: string) => {
    const cents = parseMoney(raw);
    if (cents === null) return;
    const gstFree = invoice.gst_cents === 0;
    if (mode === 'ex' && !gstFree) {
      void patch({ amount_cents: grossFromEx(cents), gst_cents: gstOnEx(cents) });
    } else if (gstFree) {
      void patch({ amount_cents: cents, gst_cents: 0 });
    } else {
      void patch({ amount_cents: cents });
    }
  };

  const stamp = (ms: number) => formatDate(new Date(ms).toISOString().slice(0, 10));

  return (
    <div className="page-body">
      <div className="inv-m">
        {/* ── Header (mockup §8.2 F) ──────────────────────────────────── */}
        <div className="inv-head">
          <div className="inv-head-row">
            <Link className="inv-back" to="/finance">
              <BackIcon />
              Finance
            </Link>
            <button
              type="button"
              className={`ib${menuOpen ? ' on' : ''}`}
              aria-label="Invoice actions"
              onClick={() => setMenuOpen(true)}
            >
              <DotsIcon />
            </button>
          </div>
          <h1 className="inv-title">
            Invoice{' '}
            <button type="button" className="inv-num" onClick={onOpenNumber} aria-label="Change invoice number">
              {number}
            </button>
          </h1>
          <div className="inv-sub">{context}</div>
          <div className="inv-pills">
            <button type="button" onClick={() => setSub({ kind: 'status' })} aria-label="Change status">
              <InvoiceKindChip kind={invoice.invoice_kind} /><Pill tone={statusTone}>
                {statusLabel}
                <span className="caret">▾</span>
              </Pill>
            </button>
            <Pill tone={effectiveBrand === 'hsp' ? 'accent' : undefined}>{effectiveBrand}</Pill>
          </div>
        </div>

        <div className="inv-gh">Public description</div>
        <button
          type="button"
          className={`inv-nb${invoice.description ? '' : ' ph'}`}
          onClick={() =>
            openField({
              title: 'Public description',
              context: `Invoice ${number}`,
              kind: 'textarea',
              value: invoice.description ?? '',
              placeholder: 'e.g. Deposit — 2-day shoot package',
              onSave: (raw) => void patch({ description: raw.trim() || null }),
            })
          }
        >
          {invoice.description ?? 'Shown on the public invoice page — tap to add.'}
        </button>
        {linesSection}

        {/* ── Amount card — danger chrome only while overdue ──────────── */}
        <div className={`inv-amount${status === 'overdue' ? ' dng' : ''}`}>
          <div className="k">{amountLabel(status, mode)}</div>
          <div className="big">
            {formatMoneyExact(displayCents(invoice.amount_cents, invoice.gst_cents, mode))}
          </div>
          <div className="grid">
            {figures.map((figure) => (
              <div key={figure.label}>
                <div className="k">{figure.label}</div>
                <div className={`v${figure.tone ? ` ${figure.tone}` : ''}`}>{figure.value}</div>
              </div>
            ))}
          </div>
          {overdueDays !== null && (
            <div className="warn">
              ⚠ {overdueDays} day{overdueDays === 1 ? '' : 's'} overdue
            </div>
          )}
        </div>

        {/* ── The two actions worth a button; the rest live in "···" ──── */}
        {(can('record-payment') || can('send-reminder') || can('send-invoice')) && (
          <div className="inv-primary">
            {can('record-payment') && (
              <button
                type="button"
                className="cbtn wide pri"
                onClick={() => void patch({ status: 'paid' })}
              >
                Record payment →
              </button>
            )}
            {can('send-reminder') ? (
              <button
                type="button"
                className="cbtn wide"
                disabled={reminderBusy}
                onClick={() => void sendReminder()}
              >
                {reminderBusy ? 'Logging…' : 'Send reminder'}
              </button>
            ) : (
              can('send-invoice') && (
                <button
                  type="button"
                  className="cbtn wide"
                  disabled={sendBusy}
                  onClick={() => void sendInvoiceEmail()}
                >
                  {sendBusy ? 'Sending…' : 'Send invoice'}
                </button>
              )
            )}
          </div>
        )}

        {/* ── Invoice classification ────────────────────────────────── */}
        <div className="inv-gh">Kind</div>
        <div className="inv-kind-field"><InvoiceKindSelect value={invoice.invoice_kind} onChange={invoice_kind => void patch({ invoice_kind })} /></div>

        {/* ── Notes — internal, autosaving (the desktop block, restyled) ─ */}
        <NotesBlock notes={invoice.notes} onSave={(notes) => void patch({ notes })} />

        {/* ── Details: the desktop rail, in rail order ────────────────── */}
        <div className="inv-gh">Details</div>
        <div className="inv-fl">
          <FieldRow
            label="Status"
            value={<Pill tone={statusTone}>{statusLabel}</Pill>}
            onPress={() => setSub({ kind: 'status' })}
          />
          {invoice.project_id ? (
            <FieldRow
              label="Brand"
              mono
              value={
                <>
                  {effectiveBrand} <span className="inv-faint">· project</span>
                </>
              }
            />
          ) : (
            <div className="fieldrow inv-segrow">
              <span className="fieldrow-k">Brand</span>
              <Seg<Brand>
                options={[
                  { value: 'hsp', label: 'HSP' },
                  { value: 'ss', label: 'SS' },
                ]}
                value={invoice.brand}
                onChange={(brand) => void patch({ brand })}
              />
            </div>
          )}
          <FieldRow
            label={mode === 'ex' ? 'Amount · ex GST' : 'Amount'}
            mono
            tone="gold"
            value={formatMoneyExact(displayCents(invoice.amount_cents, invoice.gst_cents, mode))}
            onPress={invoice.lines?.length ? undefined : () =>
              openField({
                title: mode === 'ex' ? 'Amount · ex GST' : 'Amount',
                context: `Invoice ${number}`,
                kind: 'money',
                value: String(displayCents(invoice.amount_cents, invoice.gst_cents, mode) / 100),
                onSave: saveAmount,
              })
            }
          />
          <FieldRow
            label="GST"
            mono
            value={formatMoneyExact(invoice.gst_cents)}
            onPress={invoice.lines?.length ? undefined : () =>
              openField({
                title: 'GST',
                context: `Invoice ${number}`,
                kind: 'money',
                value: String(invoice.gst_cents / 100),
                onSave: (raw) => {
                  const gst_cents = parseMoney(raw);
                  if (gst_cents !== null) void patch({ gst_cents });
                },
              })
            }
          />
          <FieldRow
            label="Issued"
            mono
            value={formatDate(invoice.issue_date)}
            empty={!invoice.issue_date}
            onPress={() =>
              openDate({
                title: 'Issue date',
                context: `Invoice ${number}`,
                value: invoice.issue_date,
                onPick: (issue_date) => void patch({ issue_date }),
              })
            }
          />
          <FieldRow
            label="Due"
            mono
            tone={status === 'overdue' ? 'danger' : undefined}
            value={formatDate(invoice.due_date)}
            empty={!invoice.due_date}
            onPress={() =>
              openDate({
                title: 'Due date',
                context: `Invoice ${number}`,
                value: invoice.due_date,
                onPick: (due_date) => void patch({ due_date }),
              })
            }
          />
          {invoice.paid_date && (
            <FieldRow
              label="Paid"
              mono
              value={formatDate(invoice.paid_date)}
              onPress={() =>
                openDate({
                  title: 'Paid date',
                  context: `Invoice ${number}`,
                  value: invoice.paid_date,
                  allowClear: true,
                  onPick: (paid_date) => void patch({ paid_date }),
                })
              }
            />
          )}
          {invoice.project_id ? (
            /* Project-linked: the client follows the project, so the row is a
               read-only link with the desktop rail's own note under it. */
            <div className="inv-linked">
              <FieldRow
                label="Client"
                tone="acc"
                value={<Link to={`/clients/${invoice.client_id}`}>{invoice.client_name} ↗</Link>}
              />
              <div className="inv-note">follows the project — relink it below</div>
            </div>
          ) : (
            <FieldRow
              label="Client"
              value={invoice.client_name}
              onPress={() => {
                ensureClients();
                setSub({ kind: 'client' });
              }}
            />
          )}
          <FieldRow
            label="Project"
            tone={invoice.project_id ? 'acc' : undefined}
            value={
              invoice.project_id ? (
                <>
                  {[formatProjectNumber(invoice.project_number), invoice.project_name]
                    .filter(Boolean)
                    .join(' ')}{' '}
                  <Link to={`/projects/${invoice.project_id}`}>↗</Link>
                </>
              ) : (
                '— No project'
              )
            }
            onPress={() => {
              ensureProjects();
              setSub({ kind: 'project' });
            }}
          />
          <FieldRow
            label="Contact"
            value={invoice.contact_name ?? invoice.client_contact_name}
            empty={!invoice.contact_name && !invoice.client_contact_name}
            onPress={() =>
              openField({
                title: 'Contact',
                context: invoice.client_name,
                kind: 'text',
                value: invoice.contact_name ?? '',
                placeholder: invoice.client_contact_name ?? 'Name',
                onSave: (raw) => void patch({ contact_name: raw.trim() || null }),
              })
            }
          />
          <FieldRow
            label="Email"
            mono
            tone="acc"
            value={invoice.contact_email ?? invoice.client_contact_email}
            empty={!invoice.contact_email && !invoice.client_contact_email}
            onPress={() =>
              openField({
                title: 'Email',
                context: invoice.client_name,
                kind: 'email',
                value: invoice.contact_email ?? '',
                placeholder: invoice.client_contact_email ?? 'name@company.com',
                onSave: (raw) => void patch({ contact_email: raw.trim() || null }),
              })
            }
          />
          <FieldRow
            label="Phone"
            mono
            value={invoice.contact_phone ?? invoice.client_contact_phone}
            empty={!invoice.contact_phone && !invoice.client_contact_phone}
            onPress={() =>
              openField({
                title: 'Phone',
                context: invoice.client_name,
                kind: 'tel',
                value: invoice.contact_phone ?? '',
                placeholder: invoice.client_contact_phone ?? '04…',
                onSave: (raw) => void patch({ contact_phone: raw.trim() || null }),
              })
            }
          />
          <FieldRow
            label="External link"
            mono
            tone={invoice.external_url ? 'acc' : undefined}
            value={invoice.external_url ? linkLabel(invoice.external_url) : ''}
            empty={!invoice.external_url}
            onPress={() =>
              openField({
                title: 'External link',
                context: `Invoice ${number}`,
                kind: 'url',
                value: invoice.external_url ?? '',
                placeholder: 'https://…',
                onSave: (raw) => void patch({ external_url: raw.trim() || null }),
              })
            }
          />
        </div>

        <div className="inv-stamp">
          <span>Created {stamp(invoice.created_at)}</span>
          {invoice.sent_at && <span>Emailed {stamp(invoice.sent_at)}</span>}
          {invoice.last_reminder_at && <span>Last reminder {stamp(invoice.last_reminder_at)}</span>}
        </div>

        <div className="inv-groups">
          <AccordionGroup slug="invoice-timeline" title="Timeline" defaultOpen={false}>
            <Timeline>
              {activity.map((entry) => (
                <TimelineRow key={entry.id} date={tlDate(entry.created_at)}>
                  {entry.summary}
                </TimelineRow>
              ))}
              {!activity.some((a) => a.kind === 'invoice.created') && (
                <TimelineRow date={tlDate(invoice.created_at)}>Invoice {number} created</TimelineRow>
              )}
            </Timeline>
          </AccordionGroup>
        </div>
      </div>

      {/* "Copy public link" and "Send invoice" run from the "···" sheet, which
          dismisses on select — their result gets a transient line instead. */}
      {actionNote && <div className="inv-toast">{actionNote}</div>}

      {/* ── Sheets ─────────────────────────────────────────────────────── */}
      <PageMenu open={menuOpen} onClose={() => setMenuOpen(false)} items={menuItems} />

      {fieldEdit && (
        <FieldSheet
          open
          title={fieldEdit.title}
          context={fieldEdit.context}
          kind={fieldEdit.kind}
          value={fieldEdit.value}
          placeholder={fieldEdit.placeholder}
          onSave={fieldEdit.onSave}
          onClose={() => setFieldEdit(null)}
        />
      )}
      {dateEdit && (
        <DateSheet
          open
          title={dateEdit.title}
          context={dateEdit.context}
          value={dateEdit.value}
          allowClear={dateEdit.allowClear}
          onPick={dateEdit.onPick}
          onClose={() => setDateEdit(null)}
        />
      )}

      {sub?.kind === 'status' && (
        <PickerSheet
          open
          title="Status"
          items={statusItems.map((item) => ({ value: item.value, label: item.label }))}
          value={invoice.status}
          onClose={closeSub}
          onSelect={(next) => {
            if (next !== invoice.status) void patch({ status: next });
          }}
        />
      )}
      {sub?.kind === 'client' && (
        <PickerSheet
          open
          title="Client"
          searchable
          items={(clients ?? []).map((client) => ({ value: client.id, label: client.name }))}
          value={invoice.client_id}
          onClose={closeSub}
          onSelect={(client_id) => {
            if (client_id !== invoice.client_id) void patch({ client_id });
          }}
        />
      )}
      {sub?.kind === 'project' && (
        <PickerSheet
          open
          title="Project"
          searchable
          items={[
            { value: NO_PROJECT_VALUE, label: '— No project' },
            ...(projects ?? []).map((project) => ({
              value: project.id,
              label: [formatProjectNumber(project.project_number), project.name]
                .filter(Boolean)
                .join(' · '),
              sub: project.client_name,
            })),
          ]}
          value={invoice.project_id ?? NO_PROJECT_VALUE}
          onClose={closeSub}
          onSelect={(project_id) => {
            if (project_id === NO_PROJECT_VALUE) {
              if (invoice.project_id) void patch({ project_id: null });
              return;
            }
            const project = projects?.find((candidate) => candidate.id === project_id);
            if (project && project.id !== invoice.project_id) {
              void patch({ project_id: project.id, client_id: project.client_id });
            }
          }}
        />
      )}

      {sub?.kind === 'void' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title="Void this invoice?"
          context={`Invoice ${number}`}
          footer={
            <>
              <button type="button" className="btn" onClick={closeSub}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  closeSub();
                  void patch({ status: 'void' });
                }}
              >
                Void invoice
              </button>
            </>
          }
        >
          <p className="inv-sheet-copy">
            It drops out of Outstanding and Overdue and stops counting towards the BAS. The row
            stays in Finance under Void — set the status back to reverse it.
          </p>
        </Sheet>
      )}
      {sub?.kind === 'trash' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title="Move invoice to trash?"
          context={`Invoice ${number}`}
          footer={
            <>
              <button type="button" className="btn" disabled={trashBusy} onClick={closeSub}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={trashBusy}
                onClick={() => void confirmTrash()}
              >
                {trashBusy ? 'Moving…' : 'Move to trash'}
              </button>
            </>
          }
        >
          <p className="inv-sheet-copy">
            It leaves every list and lands in Settings → Trash, where you can restore it. Nothing is
            deleted for good.
          </p>
          {trashError && <p className="inv-sheet-copy error" role="alert">{trashError}</p>}
        </Sheet>
      )}
    </div>
  );
}
